<template>
  <div>
    <v-card flat class="rounded-20">
      <v-card-title class="title-font"> ORDER FORM </v-card-title>
      <v-card-text align="center" v-if="initialising">
        <LoadingSpinner />
      </v-card-text>
      <v-card-text v-if="!initialising">
        <v-form ref="uploadScansForm">
          <NewOrderHasGaugeScans v-model="orderForm.gauge_scans" />
          <v-slide-y-transition mode="out-in">
            <ScanGaugeKitSelector
              v-if="orderForm.gauge_scans === true"
              v-model="orderForm.kit_box_id"
            />
            <NewOrderIsPhotogrammetry
              v-if="
                orderForm.gauge_scans === false &&
                currentTenant.photogrammetry_enabled
              "
              v-model="orderForm.is_photogrammetry"
            />
          </v-slide-y-transition>
          <NewOrderCaseType
            v-model="orderForm.immediate"
            v-if="settings.immediate_workflow"
          />

          <div v-if="!orderForm.is_photogrammetry && orderForm.gauge_scans">
            <NewOrderScanUploadAndValidate
              :loading="loading"
              :dentate="selectedOrderSession.dentate"
              :sessionId="ncSessionId"
              :leftToRight="leftToRightScan"
              :rightToLeft="rightToLeftScan"
              :leftToRightUpload="leftToRightUpload"
              :rightToLeftUpload="rightToLeftUpload"
              :validateScansRes="validateScansResponse"
              @update="updateInstance"
            />
          </div>
          <AdditionalFileUploader
            :dentate="selectedOrderSession.dentate"
            :scanFiles="scanFileUploads"
            :photoFiles="photoFileUploads"
            :otherFiles="otherFileUploads"
            @updateFiles="updateInstance"
          />

          <NewOrderOrderForm v-if="orderForm.responses" v-model="orderForm" />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          x-large
          class="mt-0 btn-success rounded-10"
          @click="submitScans"
          :loading="loading"
          :disabled="
            loading ||
            initialising ||
            !scanGaugeKits?.length ||
            (!orderForm.is_photogrammetry && !validateScansResponse)
          "
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog
      v-model="submitting"
      width="350px"
      persistent
      content-class="rounded-20"
    >
      <v-card class="pb-4">
        <v-card-title class="off">
          <h2 class="title-h2 primary--text">PROCESSING</h2>
        </v-card-title>
        <v-card-text class="text-center py-0">
          <LoadingSpinner class="mt-4" />
        </v-card-text>
        <v-card-text class="title-font pb-0 mt-2">File Uploads:</v-card-text>
        <v-card class="rounded-20 mx-4 py-2" flat outlined>
          <v-card-text class="title-font py-2"
            >Scan Gauge Scans:<v-progress-linear
              :color="scanGaugeUploadProgress === 100 ? 'success' : 'primary'"
              :value="scanGaugeUploadProgress"
            ></v-progress-linear
          ></v-card-text>
        </v-card>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { getScanAnalysis } from "@/utils"
import { classifyValidationScore } from "@osteon-nexus-connect-lib/nc-frontend"
import client from "@/lib/ApiClient"
import ScanGaugeKitSelector from "@/components/InboxPage/NewOrder/ScanGaugeKitSelector.vue"
import NewOrderCaseType from "@/components/InboxPage/NewOrder/NewOrderCaseType.vue"
import NewOrderScanUploadAndValidate from "@/components/InboxPage/NewOrder/NewOrderScanUploadAndValidate.vue"
import NewOrderHasGaugeScans from "@/components/InboxPage/NewOrder/NewOrderHasGaugeScans.vue"
import NewOrderIsPhotogrammetry from "@/components/InboxPage/NewOrder/NewOrderIsPhotogrammetry.vue"
import AdditionalFileUploader from "@/components/InboxPage/NewOrder/AdditionalFileUploader.vue"
import NewOrderOrderForm from "@/components/InboxPage/NewOrder/NewOrderOrderForm.vue"

export default {
  name: "UploadScans",

  components: {
    ScanGaugeKitSelector,
    NewOrderCaseType,
    NewOrderScanUploadAndValidate,
    NewOrderHasGaugeScans,
    NewOrderIsPhotogrammetry,
    AdditionalFileUploader,
    NewOrderOrderForm
  },

  data() {
    return {
      initialising: true,
      loading: false,
      submitting: false,
      ncSessionId: null,
      leftToRightScan: null,
      rightToLeftScan: null,
      leftToRightUpload: null,
      rightToLeftUpload: null,
      scanFileUploads: [],
      photoFileUploads: [],
      otherFileUploads: [],
      orderForm: {},
      validateScansResponse: null,
      isImmediate: false,
      failedUploads: [],
      scanGaugeUploadProgress: 0
    }
  },

  watch: {
    settings(val, prevVal) {
      if (val && this.clients && prevVal === null) {
        this.initialising = false
      }
    },

    "orderForm.gauge_scans"(val) {
      if (val && this.orderForm.is_photogrammetry)
        this.orderForm.is_photogrammetry = null
    }
  },

  computed: {
    ...mapGetters([
      "settings",
      "selectedOrder",
      "selectedOrderSession",
      "scanGaugeKits",
      "featureFlags",
      "currentTenant"
    ])
  },

  methods: {
    ...mapActions(["triggerReload"]),

    updateInstance(key, value) {
      let keyWithoutDentate = key.replace(/upper|lower/g, "")
      keyWithoutDentate =
        keyWithoutDentate[0].toLowerCase() + keyWithoutDentate.slice(1)
      this[keyWithoutDentate] = value
    },

    async submitScans() {
      if (!this.$refs.uploadScansForm.validate()) return
      if (
        this.validateScansResponse &&
        classifyValidationScore(this.validateScansResponse, this.immediate) ===
          "Fail"
      )
        return
      this.submitting = true
      this.loading = true
      await client.instance.orders
        .associateSession(
          this.selectedOrder.uid,
          this.selectedOrderSession.uid,
          {
            nc_session_id: this.ncSessionId,
            kit_box_id: this.orderForm.kit_box_id,
            scan_analysis: this.validateScansResponse
              ? getScanAnalysis(this.validateScansResponse)
              : null,
            is_photogrammetry: this.orderForm.is_photogrammetry,
            responses: this.orderForm.responses
          }
        )
        .then(async () => {
          if (this.leftToRightUpload && this.rightToLeftUpload)
            await this.uploadFiles(
              this.selectedOrder.uid,
              this.selectedOrderSession.uid,
              [this.leftToRightUpload, this.rightToLeftUpload],
              "scan",
              "scan_gauge_scan"
            )
          if (this.scanFileUploads.length) {
            await this.uploadFiles(
              this.selectedOrder.uid,
              this.selectedOrderSession.uid,
              this.scanFileUploads,
              "scan"
            )
          }
          if (this.photoFileUploads.length) {
            await this.uploadFiles(
              this.selectedOrder.uid,
              this.selectedOrderSession.uid,
              this.photoFileUploads,
              "photo"
            )
          }
          if (this.otherFileUploads.length) {
            await this.uploadFiles(
              this.selectedOrder.uid,
              this.selectedOrderSession.uid,
              this.otherFileUploads,
              "other"
            )
          }
        })
        .catch(() => {
          this.submitting = false
          this.loading = false
          return console.error("Upload Failed")
        })
      this.triggerReload("order")
      this.triggerReload("files")
      this.submitting = false
      this.loading = false
    },

    async uploadFiles(
      orderUid,
      orderSessionUid,
      files,
      fileType,
      fileCategory = null
    ) {
      if (!files.length) return

      let uploadedFiles = []
      if (!fileCategory) {
        uploadedFiles = await client.oft.uploadFiles(files)
      } else if (fileCategory === "scan_gauge_scan") {
        // Scan Gauge Scans already uploaded to oft
        uploadedFiles = files
      }

      let payload = []

      if (uploadedFiles) {
        uploadedFiles.forEach((file) => {
          if (file.uploaded) {
            let payloadObj = {
              file_name: file.oftFileName,
              file_type: fileType,
              oft_file_uid: file.oftUid
            }
            if (fileCategory) payloadObj.file_category = fileCategory

            payload.push(payloadObj)
          } else this.failedUploads.push(file)
        })
      }

      await client.instance.orders.addFiles(orderUid, orderSessionUid, payload)
    }
  },

  async mounted() {
    this.initialising = true
    if (this.settings) this.initialising = false
    else {
      this.triggerReload("settings")
    }
    if (this.scanGaugeKits.length === 1)
      this.orderForm.kit_box_id = this.scanGaugeKits[0].kit_box_id

    const previousSession = this.selectedOrder.sessions
      .filter((s) => s.dentate === this.selectedOrderSession.dentate)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[1]

    if (previousSession)
      this.$nextTick(() => {
        this.orderForm.responses = previousSession.responses
      })
    else this.orderForm.responses = this.selectedOrderSession.responses || []
  }
}
</script>
